import { gql } from '@apollo/client';

export const SALE_ORDER_FIELDS = gql`
  fragment SaleOrderFields on SaleOrder {
    id
    folio_number
    branch_id
    consulting_room_id
    consulting_room_name
    workstation_id
    customer_id
    pet_id
    veterinarian_id
    cashier_id
    status
    return_reason
    currency
    subtotal_cents
    discount_cents
    tax_cents
    rounding_cents
    total_cents
    paid_cents
    pending_cents
    calculation_version
    customer_snapshot_json
    pet_snapshot_json
    clinical_notes
    cashier_initial_notes
    doctor_medical_notes
    clinical_visit_id
    appointment_id
    sent_to_cashier_at
    returned_to_doctor_at
    taken_by_doctor_at
    paid_at
    current_weight_grams
    weight_measured_at
    weight_source
    weight_recorded_by
    created_at
    updated_at
    items {
      id
      sale_order_id
      line_no
      item_kind
      product_id
      service_id
      kit_id
      from_kit_component
      item_code
      item_name
      description
      quantity
      unit_price_cents
      tax_rate_numer
      tax_rate_denom
      tax_cents
      discount_cents
      subtotal_cents
      total_cents
      is_price_overridden
      original_unit_price_cents
      override_unit_price_cents
      price_override_reason
      price_overridden_at
      kit_item_id
      kit_item_price_override_cents
      kit_breakdown_lines {
        item_kind
        item_code
        item_name
        quantity
        unit_price_cents
        total_cents
      }
    }
  }
`;

export const MY_DOCTOR_ORDERS_QUERY = gql`
  ${SALE_ORDER_FIELDS}
  query MyDoctorOrders($offset: Int, $limit: Int, $status: SaleOrderStatus, $search: String) {
    myDoctorOrders(offset: $offset, limit: $limit, status: $status, search: $search) {
      items {
        ...SaleOrderFields
      }
      page {
        total
        offset
        limit
        has_more
      }
    }
  }
`;

export const DOCTOR_SALE_ORDER_QUERY = gql`
  ${SALE_ORDER_FIELDS}
  query DoctorSaleOrder($id: ID!) {
    doctorSaleOrder(id: $id) {
      ...SaleOrderFields
    }
  }
`;

export const PREVIEW_UNIT_PRICES_QUERY = gql`
  query PreviewUnitPrices(
    $subjects: [PreviewSubjectInput!]!
    $context: PreviewPricingContextInput
  ) {
    previewUnitPrices(subjects: $subjects, context: $context) {
      subject_kind
      subject_id
      unit_price_cents
      price_rule_id
      fallback_used
      missing_required_context
    }
  }
`;

export const PET_PROFILE_WEIGHT_QUERY = gql`
  query PetProfileWeight($id: ID!) {
    pet(id: $id) {
      id
      weight_grams
    }
  }
`;

export const SEARCH_SELLABLE_QUERY = gql`
  query SearchSellable($input: SearchSellableInput!) {
    searchSellable(input: $input) {
      kind
      id
      code
      name
      description
      price_cents
      stock_qty
      stock_min
      is_exception_handle
      is_quick_key
      tax_rate_id
      category_id
      available
      partial_kind
      requires_current_weight
    }
  }
`;

export const SELLABLE_BY_BARCODE_QUERY = gql`
  query SellableByBarcode($code: String!) {
    sellableByBarcode(code: $code) {
      kind
      id
      code
      name
      description
      price_cents
      stock_qty
      stock_min
      is_exception_handle
      is_quick_key
      tax_rate_id
      category_id
      available
      partial_kind
      requires_current_weight
    }
  }
`;

export const UPDATE_DOCTOR_DRAFT_META_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation UpdateDoctorDraftMeta($id: ID!, $input: UpdateDraftMetaInput!) {
    updateDoctorDraftMeta(id: $id, input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const ADD_DOCTOR_SALE_LINE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation AddDoctorSaleLine($input: AddSaleLineInput!) {
    addDoctorSaleLine(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const UPDATE_DOCTOR_SALE_LINE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation UpdateDoctorSaleLine($input: UpdateSaleLineInput!) {
    updateDoctorSaleLine(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const REMOVE_DOCTOR_SALE_LINE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation RemoveDoctorSaleLine($sale_order_item_id: ID!) {
    removeDoctorSaleLine(sale_order_item_id: $sale_order_item_id) {
      ...SaleOrderFields
    }
  }
`;

export const SEND_SALE_ORDER_TO_CASHIER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation SendSaleOrderToCashier($input: SendToCashierInput!) {
    sendSaleOrderToCashier(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const TAKE_DOCTOR_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation TakeDoctorOrder($input: TakeDoctorOrderInput!) {
    takeDoctorOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const RETURN_TO_CASHIER_FOR_CORRECTION_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ReturnSaleOrderToCashierForCorrection($input: ReturnToCashierForCorrectionInputGql!) {
    returnSaleOrderToCashierForCorrection(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const DOCTOR_QUEUE_BOARD_QUERY = gql`
  query DoctorQueueBoard {
    doctorQueueBoard {
      sale_order_id
      customer_id
      pet_id
      customer_name
      pet_name
      pet_age_label
      current_weight_grams
      cashier_initial_notes
      primary_item_label
      consulting_room_id
      consulting_room_name
      queued_at
    }
  }
`;

export const SET_SALE_ORDER_CURRENT_WEIGHT_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation SetSaleOrderCurrentWeight($input: SetSaleOrderCurrentWeightInputGql!) {
    setSaleOrderCurrentWeight(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const CLEAR_SALE_ORDER_CURRENT_WEIGHT_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ClearSaleOrderCurrentWeight($input: ClearSaleOrderCurrentWeightInputGql!) {
    clearSaleOrderCurrentWeight(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const APPLY_AUTHORIZED_SALE_LINE_PRICE_OVERRIDE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ApplyAuthorizedSaleLinePriceOverride(
    $input: ApplyAuthorizedSaleLinePriceOverrideInputGql!
  ) {
    applyAuthorizedSaleLinePriceOverride(input: $input) {
      ...SaleOrderFields
    }
  }
`;
